import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./Home.css";
import { APP_NAME, LOGO, FOOTER, FEATURES, PERSONAS, HERO, SECTIONS, PRICING, BLOG_LABEL } from "@shared/util/brand";
import SeoHead from "@shared/components/Seo/SeoHead";
import NewzAgentLogo from "@shared/components/Atoms/NewzAgentLogo";
import CondenseLogo from "@shared/components/Atoms/CondenseLogo";
import WebinarVaultLogo from "@shared/components/Atoms/WebinarVaultLogo";
import { BRAND, hasFeature } from "@shared/brand";
import { CONDENSE_TRIAL } from "@shared/util/constants";
import VideoBriefHome from "../Marketing/VideoBriefHome";

const isCondense = BRAND.id === "condense";
const isNewzAgent = BRAND.id === "newzagent";
const isVideoBrief = BRAND.id === "videobrief";

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

function LegacyHome() {
  const [menuOpen, setMenuOpen] = useState(false);
  const ASSET_TYPES = BRAND.marketing.assetTypes || [];
  const AD_CUE = BRAND.marketing.adCue || null;
  const [activeAssetId, setActiveAssetId] = useState(ASSET_TYPES[0]?.id || null);
  const activeAsset = ASSET_TYPES.find((a) => a.id === activeAssetId) || ASSET_TYPES[0] || null;
  useEffect(() => {
    const anchors = document.querySelectorAll('a[href^="#"]');
    anchors.forEach((anchor) => {
      anchor.addEventListener("click", function (e) {
        const href = this.getAttribute("href");
        if (href === "#" || !href.startsWith("#") || href.length === 1) return;
        e.preventDefault();
        const target = document.querySelector(href);
        if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });

    const handleScroll = () => {
      const nav = document.querySelector(".home-nav");
      if (!nav) return;
      if (isNewzAgent) {
        if (window.scrollY > 50) {
          nav.style.background = "rgba(2,6,23,.98)";
          nav.style.boxShadow = "0 2px 12px rgba(0,0,0,.3)";
        } else {
          nav.style.background = "rgba(2,6,23,.96)";
          nav.style.boxShadow = "none";
        }
      } else if (isCondense) {
        if (window.scrollY > 50) {
          nav.style.background = "rgba(17, 20, 27, 0.98)";
          nav.style.boxShadow = "0 2px 12px rgba(0,0,0,.3)";
        } else {
          nav.style.background = "rgba(17, 20, 27, 0.94)";
          nav.style.boxShadow = "none";
        }
      } else {
        if (window.scrollY > 50) {
          nav.style.background = "rgba(255,255,255,.98)";
          nav.style.boxShadow = "0 2px 12px rgba(0,0,0,.08)";
        } else {
          nav.style.background = "rgba(255,255,255,.96)";
          nav.style.boxShadow = "none";
        }
      }
    };
    window.addEventListener("scroll", handleScroll);

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((el) => {
          if (el.isIntersecting) el.target.classList.add("vis");
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll(".anim").forEach((el) => obs.observe(el));

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = isNewzAgent
    ? [
        { href: "#features", label: "Features" },
        { href: "#demo", label: "Demo" },
      ]
    : isCondense
    ? [
        { href: "#features", label: "Features" },
        ...(ASSET_TYPES.length ? [{ href: "#asset-types", label: "Asset Types" }] : []),
        ...(AD_CUE ? [{ href: "#ad-cue", label: "Ad Cues" }] : []),
        { href: "#personas", label: "Use Cases" },
        { href: "#demo", label: "Demo" },
      ]
    : [
        { href: "#features", label: "Features" },
        ...(ASSET_TYPES.length ? [{ href: "#asset-types", label: "Asset Types" }] : []),
        ...(AD_CUE ? [{ href: "#ad-cue", label: "Ad Cues" }] : []),
        { href: "#personas", label: "Use Cases" },
        { href: "#demo", label: "Demo" },
        { href: "#pricing", label: "Pricing" },
        { href: "/blog/", label: BLOG_LABEL, external: true },
      ];

  const dataBrand = isNewzAgent ? "newzagent" : isCondense ? "condense" : undefined;
  return (
    <div className="home-page" {...(dataBrand ? { "data-brand": dataBrand } : {})}>
      <SeoHead description={HERO.sub} path="/" />
      {/* Nav */}
      <nav className="home-nav">
        <div className="nav-inner">
          <Link to="/" className="logo">
            {isNewzAgent
              ? (
                <>
                  <span className="logo-icon-mobile"><NewzAgentLogo height={36} color="#ffffff" accentColor="#ef4444" showWordmark={false} /></span>
                  <span className="logo-full-desktop"><NewzAgentLogo height={36} color="#ffffff" accentColor="#ef4444" /></span>
                </>
              )
              : isCondense
              ? (
                <>
                  <span className="logo-icon-mobile"><CondenseLogo height={36} color="#F5F7F8" showWordmark={false} /></span>
                  <span className="logo-full-desktop"><CondenseLogo height={36} color="#F5F7F8" /></span>
                </>
              )
              : (
                <>
                  <img src={LOGO} alt={APP_NAME} className="logo-icon-mobile" style={{ height: 32, width: "auto" }} />
                  <WebinarVaultLogo height={44} showTagline className="logo-full-desktop" />
                </>
              )
            }
          </Link>
          <div className="nav-links">
            {navLinks.map((l) =>
              l.external
                ? <a key={l.href} href={l.href} className="nav-link" target="_blank" rel="noreferrer">{l.label}</a>
                : <a key={l.href} href={l.href} className="nav-link">{l.label}</a>
            )}
            <span style={{ display: "inline-block", width: 12 }} />
            {/* /login is served by the separate app bundle (Apache rewrite) — must be a full page load, not a client-side Link */}
            <a href="/login" className="btn-s btn-sm">Sign In</a>
            <Link to="/register" className="btn-p btn-sm">
              {isCondense ? "Sign Up Free" : isNewzAgent ? "Start Free Trial" : "Start Free Trial"}
            </Link>
          </div>
          <div className="nav-actions-mobile">
            <a href="/login" className="nav-signin-at" aria-label="Sign in">@</a>
            <button
              className="nav-hamburger"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Open menu"
            >
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                <path stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="nav-mobile-menu">
            {navLinks.map((l) =>
              l.external
                ? <a key={l.href} href={l.href} className="nav-mobile-link" target="_blank" rel="noreferrer" onClick={() => setMenuOpen(false)}>{l.label}</a>
                : <a key={l.href} href={l.href} className="nav-mobile-link" onClick={() => setMenuOpen(false)}>{l.label}</a>
            )}
            <Link to="/register" className="nav-mobile-link nav-mobile-cta" onClick={() => setMenuOpen(false)}>
              {isCondense ? "Sign Up Free" : "Start Free Trial"}
            </Link>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="hero">
        <div className="hero-glow" />
        <div className="hero-c">
          {HERO.badge && (
            <div className="hero-badge">{HERO.badge}</div>
          )}
          {isCondense && HERO.kicker ? <p className="hero-kicker">{HERO.kicker}</p> : null}
          {isNewzAgent ? (
            <h1>Stop Watching. <br /><span className="hero-highlight">Start Skimming.</span></h1>
          ) : isCondense && HERO.titleAccent && HERO.title?.includes(HERO.titleAccent) ? (
            <h1>
              {HERO.title.replace(HERO.titleAccent, "")}
              <span className="hero-signal">{HERO.titleAccent}</span>
            </h1>
          ) : (
            <h1>{HERO.title}</h1>
          )}
          <p className="sub">{HERO.sub}</p>
          {isCondense && HERO.secondary ? <p className="hero-secondary">{HERO.secondary}</p> : null}
          <div className="hero-ctas">
            <Link to="/register" className={isNewzAgent || isCondense ? "btn-p btn-lg" : "btn-p btn-lg btn-w"}>
              {isCondense ? "Sign Up Free" : isNewzAgent ? "Start Skimming for Free" : "Start Free Trial"} <ArrowIcon />
            </Link>
            <a href={ASSET_TYPES.length ? "#asset-types" : "#demo"} className="btn-s btn-lg btn-ghost">
              {ASSET_TYPES.length ? "See How It Works" : "Watch Demo"}
            </a>
          </div>
          <p className="hero-note">
            {isCondense
              ? `Upload one video free · ${CONDENSE_TRIAL.evaluationHours}-hour evaluation when your first video is ready`
              : isNewzAgent
                ? "Request a free trial · We'll get you set up"
                : "1-day free trial · No credit card required"}
          </p>
          {HERO.assetTypesLine && (
            <p className="hero-asset-types">{HERO.assetTypesLine}</p>
          )}
        </div>
      </section>

      {/* Features */}
      <section className="anim sec-alt" id="features" style={{ padding: "48px 0" }}>
        <div className="ctn">
          <h2 className="s-title">{SECTIONS.featuresTitle}</h2>
          <p className="s-sub">{SECTIONS.featuresSub}</p>
          <div className="f-grid">
            {FEATURES.map((f, i) => (
              <div className="f-card" key={i}>
                <span className="emoji">{f.emoji}</span>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Asset Types — tabbed showcase */}
      {ASSET_TYPES.length > 0 && activeAsset && (
        <section className="anim" id="asset-types" style={{ padding: "48px 0" }}>
          <div className="ctn">
            <h2 className="s-title">{isCondense ? "One pipeline. Every format." : "Built for Every Kind of Video"}</h2>
            <p className="s-sub">
              {isCondense
                ? "Sports, news, entertainment, and FAST. Each format gets its own editor, signal, and publish path."
                : "From hour-long webinars to nine-inning baseball games, each asset type has its own editor, AI prompts, and condensing controls."}
            </p>
            <div className="at-tabs" role="tablist">
              {ASSET_TYPES.map((a) => (
                <button
                  key={a.id}
                  role="tab"
                  aria-selected={a.id === activeAsset.id}
                  className={`at-tab${a.id === activeAsset.id ? " active" : ""}`}
                  onClick={() => setActiveAssetId(a.id)}
                >
                  <span className="at-tab-emoji">{a.emoji}</span>
                  <span>{a.label}</span>
                </button>
              ))}
            </div>
            <div className="at-panel">
              <div className="at-panel-head">
                <span className="at-panel-emoji">{activeAsset.emoji}</span>
                <div>
                  <h3 className="at-panel-title">{activeAsset.label}</h3>
                  <p className="at-panel-headline">{activeAsset.headline}</p>
                </div>
              </div>
              <ul className="at-bullets">
                {activeAsset.bullets.map((b, i) => (
                  <li key={i}><span className="ck">✓</span><span>{b}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Ad Cue Placement */}
      {AD_CUE && (
        <section className="anim ad-cue-section" id="ad-cue" style={{ padding: "64px 0" }}>
          <div className="ctn">
            <div className="ad-cue-grid">
              <div className="ad-cue-copy">
                <div className="ad-cue-eyebrow">Broadcast & CTV</div>
                <h2 className="ad-cue-title">{AD_CUE.title}</h2>
                <p className="ad-cue-sub">{AD_CUE.sub}</p>
                <ul className="ad-cue-bullets">
                  {AD_CUE.bullets.map((b, i) => (
                    <li key={i}><span className="ck">✓</span><span>{b}</span></li>
                  ))}
                </ul>
                {AD_CUE.cta && (
                  <Link to={AD_CUE.cta.href} className="btn-p btn-lg" style={{ marginTop: 12 }}>
                    {AD_CUE.cta.label} <ArrowIcon />
                  </Link>
                )}
              </div>
              {AD_CUE.sample && (
                <div className="ad-cue-code">
                  <div className="ad-cue-code-head">
                    <span className="ad-cue-dot" style={{ background: "#ff5f56" }} />
                    <span className="ad-cue-dot" style={{ background: "#ffbd2e" }} />
                    <span className="ad-cue-dot" style={{ background: "#27c93f" }} />
                    <span className="ad-cue-code-filename">cuepoints.json</span>
                  </div>
                  <pre className="ad-cue-code-body"><code>{AD_CUE.sample}</code></pre>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Demo */}
      <section className="anim" id="demo" style={{ padding: "48px 0" }}>
        <div className="ctn">
          <h2 className="s-title">{SECTIONS.demoTitle}</h2>
          <div className="demo-wrap">
            <div className="demo-frame">
              <iframe
                src={isCondense
                  ? "https://player.mux.com/01HjFOEWIi9tEUaNiBZiePJJlfKuZwl1N?metadata-video-title=Condense+Quick+Demo&video-title=Condense+Quick+Demo"
                  : "https://player.mux.com/sXox2LgAfDyHh01A8HMCRHqFK66JusLtq?metadata-video-title=VideoBrief+-+Demo+-+10+February+2026&video-title=VideoBrief+-+Demo+-+10+February+2026"
                }
                style={{ width: "100%", border: "none", aspectRatio: isCondense ? "16/9" : "127/90" }}
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                allowFullScreen
                title={`${APP_NAME} Demo`}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Personas (hidden for NewzAgent) */}
      {PERSONAS.length > 0 && (
        <section className="anim sec-alt" id="personas" style={{ padding: "48px 0" }}>
          <div className="ctn">
            <h2 className="s-title">{SECTIONS.personasTitle}</h2>
            <p className="s-sub">{SECTIONS.personasSub}</p>
            <div className="p-grid">
              {PERSONAS.map((p, i) => (
                <div className="p-card" key={i}>
                  <h3>{p.title}</h3>
                  <div className="tag">{p.tag}</div>
                  <p>{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing (hidden for Condense + NewzAgent) */}
      {!isCondense && !isNewzAgent && (
        <section className="anim" id="pricing" style={{ padding: "48px 0" }}>
          <div className="ctn">
            <h2 className="s-title">Simple Pricing</h2>
            <p className="s-sub">Start with a free trial, then scale as you grow.</p>
            <div className="pr-grid">
              <div className="pr-card">
                <div className="pr-tier">Starter</div>
                <div className="pr-name">Executive</div>
                <div className="pr-amt">
                  <span className="pr-price">$5</span>
                  <span className="pr-period">/month</span>
                </div>
                <ul className="pr-features">
                  <li><span className="ck">✓</span><span>1 video at a time</span></li>
                  <li><span className="ck">✓</span><span>50K monthly tokens</span></li>
                  <li><span className="ck">✓</span><span>All core features</span></li>
                  <li><span className="ck">✓</span><span>1-day free trial</span></li>
                </ul>
                <Link to="/register?plan=executive" className="btn-s" style={{ width: "100%", justifyContent: "center" }}>Start Free Trial</Link>
              </div>
              <div className="pr-card pop">
                <div className="pr-badge">Most Popular</div>
                <div className="pr-tier" style={{ marginTop: 6 }}>Professional</div>
                <div className="pr-name">Professional</div>
                <div className="pr-amt">
                  <span className="pr-price">$14</span>
                  <span className="pr-period">/month</span>
                </div>
                <ul className="pr-features">
                  <li><span className="ck">✓</span><span>Up to 50 videos in your library</span></li>
                  <li><span className="ck">✓</span><span>1M monthly tokens</span></li>
                  <li><span className="ck">✓</span><span>Auto-Condense highlight reels</span></li>
                  <li><span className="ck">✓</span><span>Embeddable video portal</span></li>
                </ul>
                <Link to="/register?plan=professional" className="btn-p" style={{ width: "100%", justifyContent: "center" }}>Get Started</Link>
              </div>
              <div className="pr-card">
                <div className="pr-tier">Enterprise</div>
                <div className="pr-name">Enterprise</div>
                <div className="pr-amt">
                  <span className="pr-price" style={{ fontSize: "1.25rem" }}>By Invitation</span>
                </div>
                <p className="text-sm text-gray-500 mb-3" style={{ marginTop: -8 }}>3-day full-access demo</p>
                <ul className="pr-features">
                  <li><span className="ck">✓</span><span>100 videos at a time</span></li>
                  <li><span className="ck">✓</span><span>10M tokens per quarter</span></li>
                  <li><span className="ck">✓</span><span>Guest access & clipping</span></li>
                  <li><span className="ck">✓</span><span>All Professional features</span></li>
                </ul>
                <Link to="/contact" className="btn-s" style={{ width: "100%", justifyContent: "center" }}>Request Demo</Link>
              </div>
            </div>
            <p className="pr-note">Additional tokens available for purchase on paid plans</p>
          </div>
        </section>
      )}

      {/* Enterprise contact — optional upsell for custom workflows */}
      {!isNewzAgent && !isCondense && (
        <section className="anim sec-alt" style={{ padding: "48px 0", textAlign: "center" }}>
          <div className="ctn">
            <h2 className="s-title">Need a Custom Setup?</h2>
            <p className="s-sub">Specialized workflows (live sports, broadcast integrations, custom limits) — contact us for tailored Enterprise pricing.</p>
            <Link to="/contact" className="btn-s btn-lg">Contact Us</Link>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="anim" style={{ padding: "48px 0", textAlign: "center" }}>
        <div className="ctn">
          {!isNewzAgent && !isCondense && <div className="final-cta-emoji">⚡</div>}
          <h2 className="s-title">{SECTIONS.finalCtaTitle}</h2>
          <p className="s-sub">{SECTIONS.finalCtaSub}</p>
          <Link to="/register" className="btn-p btn-lg">
            {isCondense ? "Sign Up Free Today" : isNewzAgent ? "Start Your Free Trial Today" : "Start Your Free Trial Today"}{" "}
            <ArrowIcon />
          </Link>
        </div>
      </section>

      {/* FAQ — GAIO-friendly chunks */}
      {!isNewzAgent && !isCondense && (
        <section className="ctn py-16 anim" id="faq">
          <h2 className="s-title mb-8">Frequently asked questions</h2>
          <div className="max-w-2xl space-y-6 text-left">
            <div>
              <h3 className="font-semibold text-lg mb-2">What is VideoBrief?</h3>
              <p className="text-gray-600 leading-relaxed">
                VideoBrief is an AI platform that turns long videos into searchable chapters, Magic Quotes for clips, and Auto-Condense highlight reels—without manual editing.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">How fast do I get results?</h3>
              <p className="text-gray-600 leading-relaxed">
                After upload, most videos produce chapters and pull quotes within minutes. Your first Magic Quote typically appears in under 60 seconds once processing starts.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Is there a free tier?</h3>
              <p className="text-gray-600 leading-relaxed">
                Yes. Start with a 1-day free trial (1 video)—no credit card required. See pricing on this page or register to upload your first video.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="home-footer">
        <div className="footer-inner">
          {FOOTER.stripped ? (
            <span>© 2025 {FOOTER.owner} · <a href={`mailto:${FOOTER.email}`}>{FOOTER.email}</a></span>
          ) : (
            <>
              <span>© 2026 {APP_NAME} · All Rights Reserved</span>
              <div className="footer-links">
                <a href="#features">Features</a>
                {ASSET_TYPES.length > 0 && <a href="#asset-types">Asset Types</a>}
                {AD_CUE && <a href="#ad-cue">Ad Cues</a>}
                {PERSONAS.length > 0 && <a href="#personas">Use Cases</a>}
                {!isCondense && !isNewzAgent && <a href="#pricing">Pricing</a>}
                {hasFeature("blog") && <a href="/blog/">{BLOG_LABEL}</a>}
                <Link to="/use-cases/webinars">Use cases</Link>
                <Link to="/privacy-policy">Privacy Policy</Link>
                <Link to="/terms-of-service">Terms of Service</Link>
              </div>
            </>
          )}
        </div>
      </footer>
    </div>
  );
}

function Home() {
  return isVideoBrief ? <VideoBriefHome /> : <LegacyHome />;
}

export default Home;
